import React from 'react';

import { Slide, ToastContainer } from 'react-toastify';
import styled from 'styled-components';

import {
  SYNMATCH_COLORS,
  SYNMATCH_HOVER_TRANSITION,
  SYNMATCH_RADII,
} from 'theme';

// One surface for every severity. Flooding the surface with the semantic colour
// forced two action inks, two hover languages and two contrast budgets, and it
// failed worst on the error surface, where an action matters most. Severity is a
// leading rail instead, so it marks the toast that needs attention without
// making a burst of them read as a fault log.
const BrandedToastContainer = styled(ToastContainer)`
  .Toastify--animate {
    animation-duration: 0.18s;
  }

  .Toastify__toast {
    align-items: center;
    background: ${SYNMATCH_COLORS.primary};
    border-radius: ${SYNMATCH_RADII.sm};
    box-shadow: 0 1rem 2.5rem rgba(0, 35, 64, 0.35),
      0 0.125rem 0.5rem rgba(0, 35, 64, 0.18);
    color: ${SYNMATCH_COLORS.text.onInverse};
    font-family: inherit;
    gap: 0.5rem;
    overflow: hidden;
    padding: 0.875rem 0.875rem 0.875rem 1rem;
    position: relative;
  }

  // The rail marks only what needs attention. On every toast it would be
  // decoration; here it carries the severity the flooded surface used to.
  .Toastify__toast--error {
    padding-left: 1.1875rem;

    &::before {
      background: ${SYNMATCH_COLORS.signal};
      bottom: 0;
      content: '';
      left: 0;
      position: absolute;
      top: 0;
      width: 0.1875rem;
    }
  }

  // Callers pass the action as a sibling of the message inside the body, so the
  // body is the flex row that puts them side by side.
  .Toastify__toast-body {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    gap: 0.5rem;
    line-height: 1.45;
    margin: 0;
    min-width: 0;
    padding: 0;
  }

  .Toastify__close-button {
    align-items: center;
    // Toastify pins the glyph to flex-start, which reads as a corner ornament on
    // a multi-line toast, and dims it to 0.7, which quietly turns the muted ink
    // into a duller colour than the token states.
    align-self: center;
    border-radius: ${SYNMATCH_RADII.pill};
    color: ${SYNMATCH_COLORS.text.onInverseMuted};
    display: flex;
    flex: 0 0 auto;
    height: 1.5rem;
    justify-content: center;
    opacity: 1;
    transition: background-color ${SYNMATCH_HOVER_TRANSITION},
      color ${SYNMATCH_HOVER_TRANSITION};
    // 24px square meets the WCAG 2.2 target-size minimum the bare glyph did not.
    width: 1.5rem;

    > svg {
      height: 0.875rem;
      width: 0.875rem;
    }

    &:hover {
      background-color: ${SYNMATCH_COLORS.surface.inverseHover};
      color: ${SYNMATCH_COLORS.text.onInverse};
    }

    &:focus-visible {
      outline: 0.125rem solid ${SYNMATCH_COLORS.text.onInverse};
      outline-offset: 0.125rem;
    }
  }

  // The action reads as a control through colour, weight and position. Against
  // the white message text the accent alone is 1.97:1, so a resting underline
  // carries the affordance and solidifying it is the hover.
  .MuiButton-root {
    align-self: center;
    border: 0;
    color: ${SYNMATCH_COLORS.accent};
    display: inline-flex;
    flex: 0 0 auto;
    font-weight: 600;
    line-height: 1.25;
    margin: 0;
    min-height: auto;
    padding: 0.25rem 0.125rem;
    text-decoration: underline;
    text-decoration-color: ${SYNMATCH_COLORS.accentMuted};
    text-underline-offset: 0.1875rem;
    transition: text-decoration-color ${SYNMATCH_HOVER_TRANSITION};
    white-space: nowrap;

    &:hover {
      background: none;
      text-decoration-color: ${SYNMATCH_COLORS.accent};
    }

    &.Mui-focusVisible {
      outline: 0.125rem solid ${SYNMATCH_COLORS.text.onInverse};
      outline-offset: 0.1875rem;
      text-decoration-color: ${SYNMATCH_COLORS.accent};
    }
  }

  // Toastify's transitions are plain CSS animations and honour nothing.
  @media (prefers-reduced-motion: reduce) {
    .Toastify__slide-enter--top-right,
    .Toastify__slide-exit--top-right {
      animation-name: none;
    }
  }
`;

export const StyledToastContainer: React.FC = () => (
  <BrandedToastContainer
    position="top-right"
    autoClose={4000}
    hideProgressBar
    newestOnTop
    closeOnClick
    pauseOnHover
    // A receipt seen dozens of times a session should not bounce.
    transition={Slide}
  />
);
